import { useEffect, useRef, useState, forwardRef } from "react";
import { useParams, useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Plus, Trash2, Save, Download, Share2, MessageSquare, ArrowLeft,
  ChevronDown, Palette, Type, Eye, Pencil,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";

const PRESETS = {
  aubergine: { accent: "#3A0F4D", label: "Aubergine" },
  rose:      { accent: "#9D2B6B", label: "Rose" },
  teal:      { accent: "#1F8E97", label: "Teal" },
  gold:      { accent: "#B98944", label: "Gold" },
  mono:      { accent: "#1f2937", label: "Mono" },
};
const FONTS = ["Playfair Display", "Outfit", "Inter", "Lora", "Source Sans Pro"];

const blankLine = () => ({ description: "", hsn: "", qty: 1, unit_price: 0, tax_rate: 18, discount_pct: 0 });
const todayISO = () => new Date().toISOString().slice(0, 10);

function calcLine(li) {
  const sub = (+li.qty || 0) * (+li.unit_price || 0);
  const disc = sub * ((+li.discount_pct || 0) / 100);
  const taxable = sub - disc;
  const tax = taxable * ((+li.tax_rate || 0) / 100);
  return { sub, disc, taxable, tax, total: taxable + tax };
}
function calcTotals(items, inter_state) {
  let subtotal = 0, discount = 0, tax = 0;
  items.forEach((li) => {
    const c = calcLine(li);
    subtotal += c.sub; discount += c.disc; tax += c.tax;
  });
  return {
    subtotal: +subtotal.toFixed(2),
    discount_total: +discount.toFixed(2),
    tax_total: +tax.toFixed(2),
    taxable: +(subtotal - discount).toFixed(2),
    grand_total: +(subtotal - discount + tax).toFixed(2),
    cgst: inter_state ? 0 : +(tax / 2).toFixed(2),
    sgst: inter_state ? 0 : +(tax / 2).toFixed(2),
    igst: inter_state ? +tax.toFixed(2) : 0,
  };
}

export default function DocumentBuilder() {
  const { user, updateProfile } = useAuth(); // updateProfile not used but kept for parity
  void updateProfile;
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previewRef = useRef(null);

  const isReadOnly = !!id && !location.pathname.endsWith("/edit");
  const docType = params.get("type") === "quotation" ? "quotation" : "invoice";

  const [doc, setDoc] = useState({
    doc_type: docType,
    issue_date: todayISO(),
    due_date: "",
    inter_state: false,
    notes: "",
    terms: docType === "invoice" ? "Payment due within 15 days. Bank transfer preferred." : "Quotation valid for 30 days from issue date.",
    recipient: { type: "external", member_id: null, name: "", business: "", email: "", phone: "", address: "", gstin: "" },
    line_items: [blankLine()],
    theme: { preset: "aubergine", accent: "#3A0F4D", font_family: "Playfair Display", show_hsn: true, show_tax: true, show_discount: false },
    issuer: null,
    number: "",
    status: "draft",
    totals: { subtotal: 0, tax_total: 0, discount_total: 0, grand_total: 0 },
  });
  const [members, setMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [savedId, setSavedId] = useState(id || null);
  const [saving, setSaving] = useState(false);

  // Load existing doc
  useEffect(() => {
    if (id) {
      api.get(`/documents/${id}`).then((r) => {
        setDoc(r.data);
      }).catch(() => { toast.error("Could not load document"); navigate("/documents"); });
    } else if (user) {
      // Use my profile as the issuer snapshot for the live preview
      setDoc((d) => ({
        ...d,
        issuer: {
          name: user.name || "",
          business: user.business || "",
          company_logo: user.company_logo || "",
          avatar: user.avatar || "",
          email: user.email || "",
          phone: user.phone || "",
          website: user.website || "",
          office_address: user.office_address || "",
          gstin: user.gstin || "",
        },
      }));
    }
    // eslint-disable-next-line
  }, [id, user]);

  // Member search
  useEffect(() => {
    if (memberQuery.length < 1 || doc.recipient.type !== "member") { setMembers([]); return; }
    const t = setTimeout(() => {
      api.get(`/members?q=${encodeURIComponent(memberQuery)}&limit=8`).then((r) => setMembers(r.data)).catch(() => setMembers([]));
    }, 250);
    return () => clearTimeout(t);
  }, [memberQuery, doc.recipient.type]);

  const totals = calcTotals(doc.line_items, doc.inter_state);

  // Mutators
  const setField = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  const setRecipient = (patch) => setDoc((d) => ({ ...d, recipient: { ...d.recipient, ...patch } }));
  const setTheme = (patch) => setDoc((d) => ({ ...d, theme: { ...d.theme, ...patch } }));
  const setLine = (idx, patch) => setDoc((d) => ({ ...d, line_items: d.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)) }));
  const addLine = () => setDoc((d) => ({ ...d, line_items: [...d.line_items, blankLine()] }));
  const removeLine = (i) => setDoc((d) => ({ ...d, line_items: d.line_items.filter((_, idx) => idx !== i) }));

  const pickMember = (m) => {
    setRecipient({ type: "member", member_id: m.id, name: m.name, business: m.business || "", email: m.email || "", phone: m.phone || "", address: m.office_address || "" });
    setMembers([]); setMemberQuery("");
  };

  const persist = async (extra = {}) => {
    setSaving(true);
    const payload = {
      doc_type: doc.doc_type,
      issue_date: doc.issue_date,
      due_date: doc.due_date,
      recipient: doc.recipient,
      line_items: doc.line_items,
      inter_state: doc.inter_state,
      theme: doc.theme,
      notes: doc.notes,
      terms: doc.terms,
      status: doc.status,
      ...extra,
    };
    try {
      let r;
      if (savedId) {
        r = await api.put(`/documents/${savedId}`, payload);
      } else {
        r = await api.post("/documents", payload);
        setSavedId(r.data.id);
        window.history.replaceState(null, "", `/documents/${r.data.id}/edit`);
      }
      setDoc(r.data);
      return r.data;
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Save failed");
      throw err;
    } finally { setSaving(false); }
  };

  const save = async () => { try { await persist(); toast.success("Saved"); } catch (e) { void e; } };

  const downloadPDF = async () => {
    const saved = await persist();
    const el = previewRef.current;
    if (!el) return;
    toast("Generating PDF…");
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const imgW = pageW - 48;
      const imgH = (canvas.height / canvas.width) * imgW;
      pdf.addImage(img, "PNG", 24, 24, imgW, imgH);
      pdf.save(`${saved.number || "document"}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF generation failed"); }
  };

  const shareWithMember = async () => {
    try {
      const saved = await persist();
      await api.post(`/documents/${saved.id}/share`);
      toast.success(`Shared with ${doc.recipient.name}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Share failed");
    }
  };

  const sendWhatsApp = async () => {
    const saved = await persist();
    const phone = (doc.recipient.phone || "").replace(/[^\d]/g, "");
    const label = saved.doc_type === "invoice" ? "Invoice" : "Quotation";
    const text = encodeURIComponent(
      `Hello ${doc.recipient.name || ""},\n\n${label} ${saved.number} for ₹${Number(saved.totals?.grand_total || 0).toLocaleString("en-IN")} is ready.\n\nFrom: ${user.business || user.name} (WOWSHE Member)\n\nAttaching the PDF — please download it first using "Download PDF" then send via WhatsApp.`
    );
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noreferrer");
  };

  const role = user?.role === "admin" ? "admin" : "member";
  const title = isReadOnly
    ? `${doc.doc_type === "invoice" ? "Invoice" : "Quotation"} ${doc.number || ""}`.trim()
    : (savedId ? `Edit ${doc.doc_type}` : `New ${doc.doc_type}`);

  return (
    <DashboardShell
      role={role}
      title={title}
      subtitle={isReadOnly ? `Issued to ${doc.recipient?.name || "—"}` : "Customise colours, fonts & rows. Live preview on the right."}
      actions={(
        <div className="flex items-center gap-2">
          <Link to="/documents" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-plum hover:text-aubergine"><ArrowLeft className="w-3.5 h-3.5" /> Back</Link>
          {!isReadOnly && <button data-testid="doc-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-aubergine/20 text-aubergine text-xs uppercase tracking-widest hover:bg-aubergine hover:text-ivory"><Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save draft"}</button>}
          <button data-testid="doc-download-btn" onClick={downloadPDF} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aubergine text-ivory text-xs uppercase tracking-widest hover:bg-aubergineLight"><Download className="w-3.5 h-3.5" /> Download PDF</button>
          {!isReadOnly && doc.recipient?.type === "member" && doc.recipient?.member_id && (
            <button data-testid="doc-share-member-btn" onClick={shareWithMember} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose text-ivory text-xs uppercase tracking-widest hover:bg-rose/85"><Share2 className="w-3.5 h-3.5" /> Share with member</button>
          )}
          {!isReadOnly && doc.recipient?.type === "external" && (
            <button data-testid="doc-whatsapp-btn" onClick={sendWhatsApp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-ivory text-xs uppercase tracking-widest hover:bg-emerald-700"><MessageSquare className="w-3.5 h-3.5" /> WhatsApp</button>
          )}
          {isReadOnly && (
            <Link to={`/documents/${savedId}/edit`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-aubergine/20 text-aubergine text-xs uppercase tracking-widest hover:bg-aubergine hover:text-ivory"><Pencil className="w-3.5 h-3.5" /> Edit</Link>
          )}
        </div>
      )}
    >
      <div data-testid="doc-builder" className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* LEFT — Editor controls */}
        {!isReadOnly && (
          <div className="xl:col-span-5 space-y-6">
            {/* Recipient */}
            <section className="bg-white rounded-2xl border border-aubergine/8 p-6">
              <p className="overline mb-3">Recipient</p>
              <div className="flex gap-2 mb-4">
                {[["external", "External"], ["member", "WOWSHE Member"]].map(([k, lbl]) => (
                  <button key={k} type="button" data-testid={`rcpt-type-${k}`} onClick={() => setRecipient({ type: k, member_id: null })} className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border ${doc.recipient.type === k ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum"}`}>{lbl}</button>
                ))}
              </div>
              {doc.recipient.type === "member" && (
                <div className="relative mb-3">
                  <input data-testid="rcpt-member-search" value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} placeholder="Search members by name or business…" className="input-luxe" />
                  {members.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-aubergine/10 rounded-xl shadow-luxe max-h-72 overflow-y-auto">
                      {members.map((m) => (
                        <button key={m.id} type="button" onClick={() => pickMember(m)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-lavender/50 text-left">
                          {m.avatar ? <img src={m.avatar} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-lavender text-plum flex items-center justify-center font-display">{m.name?.[0]}</div>}
                          <div className="min-w-0">
                            <p className="text-sm text-aubergine truncate">{m.name}</p>
                            <p className="text-xs text-plumSoft truncate">{m.business || m.industry}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {doc.recipient.member_id && (
                    <p className="text-xs text-emerald-700 mt-2">✓ Selected: {doc.recipient.name}</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldLite label="Name *" testid="rcpt-name" value={doc.recipient.name} onChange={(v) => setRecipient({ name: v })} />
                <FieldLite label="Company" testid="rcpt-business" value={doc.recipient.business || ""} onChange={(v) => setRecipient({ business: v })} />
                <FieldLite label="Email" testid="rcpt-email" value={doc.recipient.email || ""} onChange={(v) => setRecipient({ email: v })} />
                <FieldLite label="Phone" testid="rcpt-phone" value={doc.recipient.phone || ""} onChange={(v) => setRecipient({ phone: v })} />
                <FieldLite label="GSTIN" testid="rcpt-gstin" value={doc.recipient.gstin || ""} onChange={(v) => setRecipient({ gstin: v })} />
                <FieldLite label="Address" testid="rcpt-address" value={doc.recipient.address || ""} onChange={(v) => setRecipient({ address: v })} />
              </div>
            </section>

            {/* Dates */}
            <section className="bg-white rounded-2xl border border-aubergine/8 p-6">
              <p className="overline mb-3">Dates</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldLite label="Issue date" testid="doc-issue-date" type="date" value={doc.issue_date} onChange={(v) => setField("issue_date", v)} />
                <FieldLite label={doc.doc_type === "invoice" ? "Due date" : "Valid until"} testid="doc-due-date" type="date" value={doc.due_date} onChange={(v) => setField("due_date", v)} />
              </div>
              <label className="mt-4 flex items-center gap-3 text-sm text-aubergine">
                <input type="checkbox" data-testid="doc-interstate" checked={doc.inter_state} onChange={(e) => setField("inter_state", e.target.checked)} className="w-4 h-4 accent-aubergine" />
                Inter-state transaction (use IGST instead of CGST + SGST)
              </label>
            </section>

            {/* Line items */}
            <section className="bg-white rounded-2xl border border-aubergine/8 p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="overline">Line items</p>
                <button type="button" data-testid="add-line-btn" onClick={addLine} className="text-xs text-plum hover:text-aubergine inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add row</button>
              </div>
              <div className="space-y-3">
                {doc.line_items.map((li, i) => {
                  const c = calcLine(li);
                  return (
                    <div key={i} data-testid={`line-${i}`} className="p-4 rounded-xl bg-lavender/30 relative">
                      <button type="button" onClick={() => removeLine(i)} className="absolute top-3 right-3 text-plum hover:text-rose p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      <FieldLite label="Description *" testid={`line-desc-${i}`} value={li.description} onChange={(v) => setLine(i, { description: v })} />
                      <div className={`mt-2 grid grid-cols-2 ${doc.theme.show_hsn ? "md:grid-cols-5" : "md:grid-cols-4"} gap-2`}>
                        {doc.theme.show_hsn && <FieldLite label="HSN/SAC" testid={`line-hsn-${i}`} value={li.hsn || ""} onChange={(v) => setLine(i, { hsn: v })} />}
                        <FieldLite label="Qty" testid={`line-qty-${i}`} type="number" value={li.qty} onChange={(v) => setLine(i, { qty: parseFloat(v) || 0 })} />
                        <FieldLite label="Rate (₹)" testid={`line-price-${i}`} type="number" value={li.unit_price} onChange={(v) => setLine(i, { unit_price: parseFloat(v) || 0 })} />
                        {doc.theme.show_discount && <FieldLite label="Disc %" testid={`line-disc-${i}`} type="number" value={li.discount_pct} onChange={(v) => setLine(i, { discount_pct: parseFloat(v) || 0 })} />}
                        {doc.theme.show_tax && <FieldLite label="GST %" testid={`line-tax-${i}`} type="number" value={li.tax_rate} onChange={(v) => setLine(i, { tax_rate: parseFloat(v) || 0 })} />}
                      </div>
                      <p className="text-xs text-plumSoft mt-2 text-right">Line total: ₹ {c.total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Notes & terms */}
            <section className="bg-white rounded-2xl border border-aubergine/8 p-6 space-y-3">
              <p className="overline">Notes & terms</p>
              <FieldLite label="Notes (visible on document)" testid="doc-notes" multiline value={doc.notes} onChange={(v) => setField("notes", v)} />
              <FieldLite label="Terms & conditions" testid="doc-terms" multiline value={doc.terms} onChange={(v) => setField("terms", v)} />
            </section>

            {/* Theme */}
            <section className="bg-white rounded-2xl border border-aubergine/8 p-6">
              <div className="flex items-center gap-2 mb-3"><Palette className="w-4 h-4 text-rose" /><p className="overline">Theme</p></div>
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(PRESETS).map(([k, v]) => (
                  <button key={k} type="button" data-testid={`theme-${k}`} onClick={() => setTheme({ preset: k, accent: v.accent })} className={`px-3 py-2 rounded-full text-xs uppercase tracking-widest border flex items-center gap-2 ${doc.theme.preset === k ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum"}`}>
                    <span className="w-3 h-3 rounded-full" style={{ background: v.accent }} />
                    {v.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-luxe">Accent colour</label>
                  <input type="color" data-testid="theme-accent" value={doc.theme.accent} onChange={(e) => setTheme({ accent: e.target.value })} className="h-10 w-full rounded-lg border border-aubergine/15 cursor-pointer" />
                </div>
                <div>
                  <label className="label-luxe flex items-center gap-1"><Type className="w-3 h-3" /> Font</label>
                  <select data-testid="theme-font" value={doc.theme.font_family} onChange={(e) => setTheme({ font_family: e.target.value })} className="input-luxe">
                    {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-4">
                {[["show_hsn", "HSN/SAC column"], ["show_tax", "GST column"], ["show_discount", "Discount column"]].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-aubergine">
                    <input type="checkbox" data-testid={`theme-${k}`} checked={doc.theme[k]} onChange={(e) => setTheme({ [k]: e.target.checked })} className="w-4 h-4 accent-aubergine" />
                    {lbl}
                  </label>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* RIGHT — Live preview */}
        <div className={isReadOnly ? "xl:col-span-12" : "xl:col-span-7"}>
          <div className="bg-white rounded-2xl border border-aubergine/8 shadow-luxe p-2 sticky top-4">
            <div className="flex items-center justify-between px-4 py-2 text-xs text-plum">
              <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Live preview</span>
              <span className="text-plumSoft">{doc.number || "Auto-numbered on save"}</span>
            </div>
            <DocumentPreview
              ref={previewRef}
              doc={doc}
              totals={totals}
              issuer={doc.issuer}
            />
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

// ---------- Preview ----------
function DocumentPreviewBase({ doc, totals, issuer }, ref) {
  const { theme } = doc;
  const accent = theme?.accent || "#3A0F4D";
  const fontFam = `"${theme?.font_family || "Playfair Display"}", serif`;
  const issuedLabel = doc.doc_type === "invoice" ? "INVOICE" : "QUOTATION";

  return (
    <div ref={ref} className="bg-white p-10 text-[13px] text-gray-800" style={{ fontFamily: fontFam, minHeight: 900 }}>
      {/* Header */}
      <header className="flex items-start justify-between pb-6 border-b" style={{ borderColor: accent + "30" }}>
        <div className="flex items-start gap-4">
          {issuer?.company_logo ? (
            <img src={issuer.company_logo} alt="logo" className="h-14 w-auto max-w-[160px] object-contain" crossOrigin="anonymous" />
          ) : issuer?.avatar ? (
            <img src={issuer.avatar} alt="logo" className="h-14 w-14 rounded-full object-cover" crossOrigin="anonymous" />
          ) : null}
          <div>
            <p className="font-semibold text-lg" style={{ color: accent }}>{issuer?.business || issuer?.name || "Your business"}</p>
            <p className="text-[11px] text-gray-500 whitespace-pre-line mt-1">{issuer?.office_address}</p>
            <p className="text-[11px] text-gray-500 mt-1">{[issuer?.phone, issuer?.email, issuer?.website].filter(Boolean).join(" · ")}</p>
            {issuer?.gstin && <p className="text-[11px] text-gray-500 mt-0.5">GSTIN: {issuer.gstin}</p>}
          </div>
        </div>
        <div className="text-right">
          <h1 className="text-3xl tracking-[0.2em]" style={{ color: accent }}>{issuedLabel}</h1>
          <p className="text-sm text-gray-600 mt-2">{doc.number || "—"}</p>
          <p className="text-xs text-gray-500 mt-3">Date: <span className="text-gray-700">{doc.issue_date}</span></p>
          {doc.due_date && <p className="text-xs text-gray-500">{doc.doc_type === "invoice" ? "Due" : "Valid until"}: <span className="text-gray-700">{doc.due_date}</span></p>}
        </div>
      </header>

      {/* Bill to */}
      <section className="mt-6 grid grid-cols-2 gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-400">Bill to</p>
          <p className="font-semibold mt-1" style={{ color: accent }}>{doc.recipient?.name || "—"}</p>
          {doc.recipient?.business && <p className="text-xs text-gray-600">{doc.recipient.business}</p>}
          <p className="text-[11px] text-gray-500 whitespace-pre-line mt-1">{doc.recipient?.address}</p>
          <p className="text-[11px] text-gray-500 mt-1">{[doc.recipient?.email, doc.recipient?.phone].filter(Boolean).join(" · ")}</p>
          {doc.recipient?.gstin && <p className="text-[11px] text-gray-500">GSTIN: {doc.recipient.gstin}</p>}
        </div>
      </section>

      {/* Line items table */}
      <table className="w-full mt-7 text-[12px]">
        <thead>
          <tr style={{ background: accent, color: "#fff" }}>
            <th className="text-left p-3">#</th>
            <th className="text-left p-3">Description</th>
            {theme?.show_hsn && <th className="text-left p-3">HSN/SAC</th>}
            <th className="text-right p-3">Qty</th>
            <th className="text-right p-3">Rate</th>
            {theme?.show_discount && <th className="text-right p-3">Disc%</th>}
            {theme?.show_tax && <th className="text-right p-3">GST%</th>}
            <th className="text-right p-3">Amount</th>
          </tr>
        </thead>
        <tbody>
          {doc.line_items.map((li, i) => {
            const c = calcLine(li);
            return (
              <tr key={i} className="border-b" style={{ borderColor: accent + "20" }}>
                <td className="p-3 text-gray-500">{i + 1}</td>
                <td className="p-3 text-gray-800">{li.description || "—"}</td>
                {theme?.show_hsn && <td className="p-3 text-gray-600">{li.hsn || "—"}</td>}
                <td className="p-3 text-right">{li.qty}</td>
                <td className="p-3 text-right">₹ {Number(li.unit_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                {theme?.show_discount && <td className="p-3 text-right">{li.discount_pct}%</td>}
                {theme?.show_tax && <td className="p-3 text-right">{li.tax_rate}%</td>}
                <td className="p-3 text-right font-medium">₹ {c.total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <section className="mt-6 flex justify-end">
        <div className="w-72 text-[12px] space-y-1.5">
          <Row label="Subtotal" value={totals.subtotal} />
          {totals.discount_total > 0 && <Row label="Discount" value={-totals.discount_total} />}
          <Row label="Taxable" value={totals.taxable} />
          {doc.inter_state ? (
            <Row label="IGST" value={totals.igst} />
          ) : (
            <>
              <Row label="CGST" value={totals.cgst} />
              <Row label="SGST" value={totals.sgst} />
            </>
          )}
          <div className="border-t mt-2 pt-2 flex justify-between" style={{ borderColor: accent + "40" }}>
            <span className="font-semibold" style={{ color: accent }}>Grand total</span>
            <span className="font-semibold text-base" style={{ color: accent }}>₹ {Number(totals.grand_total).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </section>

      {/* Notes & terms */}
      {(doc.notes || doc.terms) && (
        <section className="mt-10 pt-6 border-t grid grid-cols-2 gap-6 text-[11px] text-gray-600" style={{ borderColor: accent + "20" }}>
          {doc.notes && <div><p className="uppercase tracking-widest text-[10px] text-gray-400 mb-1">Notes</p><p className="whitespace-pre-line">{doc.notes}</p></div>}
          {doc.terms && <div><p className="uppercase tracking-widest text-[10px] text-gray-400 mb-1">Terms</p><p className="whitespace-pre-line">{doc.terms}</p></div>}
        </section>
      )}

      <p className="mt-10 text-center text-[10px] text-gray-400">Issued via WOWSHE · Women Of Wisdom, Success, Happiness & Empowerment</p>
    </div>
  );
}
const DocumentPreview = forwardRef(DocumentPreviewBase);
function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800">₹ {Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
    </div>
  );
}

function FieldLite({ label, value, onChange, testid, type = "text", multiline = false }) {
  return (
    <div>
      <label className="label-luxe">{label}</label>
      {multiline ? (
        <textarea rows={3} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-luxe" />
      ) : (
        <input type={type} data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="input-luxe" />
      )}
    </div>
  );
}
